import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ProfileData {
  full_name: string;
  cpa_license_number: string;
  license_state: string;
  years_in_practice: number;
  court_testimony_count: number;
  certifications: string[];
  degrees: Array<{
    degree: string;
    field: string;
    university: string;
    year?: number;
  }>;
  specializations: string[];
  professional_memberships: string[];
  bio_statement: string;
}

export default function AccountantProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasProfile, setHasProfile] = useState(false);
  
  const [formData, setFormData] = useState<ProfileData>({
    full_name: '',
    cpa_license_number: '',
    license_state: '',
    years_in_practice: 0,
    court_testimony_count: 0,
    certifications: [],
    degrees: [],
    specializations: [],
    professional_memberships: [],
    bio_statement: ''
  });

  const [newCert, setNewCert] = useState('');
  const [newSpec, setNewSpec] = useState('');
  const [newMembership, setNewMembership] = useState('');
  const [newDegree, setNewDegree] = useState({ degree: '', field: '', university: '', year: '' });

  // Get auth header
  const getAuthHeader = () => {
    const token = localStorage.getItem('accountant_token');
    return token ? { 'X-Accountant-Token': token } : {};
  };

  // Load existing profile
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/accountant-profile/me`,
        { headers: getAuthHeader() }
      );
      
      setFormData(response.data);
      setHasProfile(true);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No profile yet - that's okay
        setHasProfile(false);
      } else {
        console.error('Error loading profile:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/v1/accountant-profile/create`,
        formData,
        { headers: getAuthHeader() }
      );
      
      setSaveMessage('Profile saved successfully! You can now generate court-ready expert reports.');
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError('Failed to save profile. Please check all required fields.');
    } finally {
      setSaving(false);
    }
  };

  const addCertification = () => {
    if (newCert.trim()) {
      setFormData({ ...formData, certifications: [...formData.certifications, newCert.trim()] });
      setNewCert('');
    }
  };

  const removeCertification = (index: number) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index)
    });
  };

  const addSpecialization = () => {
    if (newSpec.trim()) {
      setFormData({ ...formData, specializations: [...formData.specializations, newSpec.trim()] });
      setNewSpec('');
    }
  };

  const removeSpecialization = (index: number) => {
    setFormData({
      ...formData,
      specializations: formData.specializations.filter((_, i) => i !== index)
    });
  };

  const addMembership = () => {
    if (newMembership.trim()) {
      setFormData({ ...formData, professional_memberships: [...formData.professional_memberships, newMembership.trim()] });
      setNewMembership('');
    }
  };

  const removeMembership = (index: number) => {
    setFormData({
      ...formData,
      professional_memberships: formData.professional_memberships.filter((_, i) => i !== index)
    });
  };

  const addDegree = () => {
    if (newDegree.degree && newDegree.field && newDegree.university) {
      setFormData({
        ...formData,
        degrees: [...formData.degrees, {
          degree: newDegree.degree,
          field: newDegree.field,
          university: newDegree.university,
          year: newDegree.year ? parseInt(newDegree.year) : undefined
        }]
      });
      setNewDegree({ degree: '', field: '', university: '', year: '' });
    }
  };

  const removeDegree = (index: number) => {
    setFormData({
      ...formData,
      degrees: formData.degrees.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {hasProfile ? 'Update Your Profile' : 'Complete Your Profile'}
          </h1>
          <p className="text-gray-600">
            Your credentials will be automatically included in all expert reports you generate, making them court-ready.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          
          {/* Basic Information */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPA License Number *
                  </label>
                  <input
                    type="text"
                    value={formData.cpa_license_number}
                    onChange={(e) => setFormData({ ...formData, cpa_license_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="12345"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License State *
                  </label>
                  <input
                    type="text"
                    value={formData.license_state}
                    onChange={(e) => setFormData({ ...formData, license_state: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="TX"
                    maxLength={2}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years in Practice *
                  </label>
                  <input
                    type="number"
                    value={formData.years_in_practice}
                    onChange={(e) => setFormData({ ...formData, years_in_practice: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="15"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Court Testimony Cases
                  </label>
                  <input
                    type="number"
                    value={formData.court_testimony_count}
                    onChange={(e) => setFormData({ ...formData, court_testimony_count: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="25"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Education */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Education</h2>
            
            <div className="space-y-2 mb-4">
              {formData.degrees.map((deg, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg">
                  <span className="text-sm">
                    {deg.degree} in {deg.field} - {deg.university} {deg.year && `(${deg.year})`}
                  </span>
                  <button
                    onClick={() => removeDegree(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-2">
              <input
                type="text"
                value={newDegree.degree}
                onChange={(e) => setNewDegree({ ...newDegree, degree: e.target.value })}
                placeholder="Degree (e.g., Bachelor of Science)"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                value={newDegree.field}
                onChange={(e) => setNewDegree({ ...newDegree, field: e.target.value })}
                placeholder="Field (e.g., Accounting)"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={newDegree.university}
                onChange={(e) => setNewDegree({ ...newDegree, university: e.target.value })}
                placeholder="University"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                value={newDegree.year}
                onChange={(e) => setNewDegree({ ...newDegree, year: e.target.value })}
                placeholder="Year (optional)"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              onClick={addDegree}
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              + Add Degree
            </button>
          </div>

          {/* Certifications */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Certifications</h2>
            <p className="text-sm text-gray-600 mb-2">e.g., ABV, CVA, CFE, CFF, MAFF</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.certifications.map((cert, index) => (
                <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span>{cert}</span>
                  <button
                    onClick={() => removeCertification(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newCert}
                onChange={(e) => setNewCert(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                placeholder="Enter certification"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addCertification}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Specializations */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Areas of Specialization</h2>
            <p className="text-sm text-gray-600 mb-2">e.g., Business Interruption, Forensic Accounting, Economic Damages</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.specializations.map((spec, index) => (
                <div key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span>{spec}</span>
                  <button
                    onClick={() => removeSpecialization(index)}
                    className="text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newSpec}
                onChange={(e) => setNewSpec(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
                placeholder="Enter specialization"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addSpecialization}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Professional Memberships */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Professional Memberships</h2>
            <p className="text-sm text-gray-600 mb-2">e.g., AICPA, State CPA Society, NACVA</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.professional_memberships.map((membership, index) => (
                <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span>{membership}</span>
                  <button
                    onClick={() => removeMembership(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newMembership}
                onChange={(e) => setNewMembership(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addMembership()}
                placeholder="Enter organization"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={addMembership}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Bio Statement */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Professional Bio (Optional)</h2>
            <textarea
              value={formData.bio_statement}
              onChange={(e) => setFormData({ ...formData, bio_statement: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Brief professional biography or summary of expertise..."
            />
          </div>

          {/* Save Button */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.full_name || !formData.cpa_license_number || !formData.license_state}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : hasProfile ? 'Update Profile' : 'Save Profile & Start Generating Reports'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
